import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  inject,
  input,
  signal,
} from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  HttpErrorService,
  NavigatorService,
  Route,
  RouteParam,
} from '@web/common';
import {
  BoxQuestionnaireAvailabilityComponent,
  BoxQuestionnaireStatus,
  PublicBoxQuestionnaireDataSourceService,
  PublicBoxQuestionnaireCounterService,
  BoxQuestionnaireDetails,
  BoxQuestionnaireQuestion,
  BoxQuestionnaireQuestionOption,
} from '@web/shared/module/shop';
import {
  AlertsComponent,
  AlertsService,
  Icon,
  IconComponent,
  ImageThumbnailComponent,
  SlimButtonComponent,
  SpinnerFilledComponent,
  SlimButtonTheme,
  ToastService,
} from '@web/shared/ui';
import { Guid, isNil, Nillable } from '@web/utils';
import { filter, switchMap } from 'rxjs';

type QuestionnaireSelections = Record<string, Nillable<Guid>>;

@Component({
  selector: 'app-public-box-questionnaire-details',
  imports: [
    AlertsComponent,
    BoxQuestionnaireAvailabilityComponent,
    ImageThumbnailComponent,
    NgOptimizedImage,
    SpinnerFilledComponent,
    IconComponent,
    SlimButtonComponent,
  ],
  templateUrl: './public-box-questionnaire-details.component.html',
  styleUrl: './public-box-questionnaire-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AlertsService],
})
export class PublicBoxQuestionnaireDetailsComponent {
  public readonly previewMode = input(false);
  public readonly questionnaireId = input.required<Guid>();

  private readonly alerts = inject(AlertsService);
  // prettier-ignore
  private readonly boxQuestionnaireDataSource = inject(PublicBoxQuestionnaireDataSourceService);
  // prettier-ignore
  private readonly questionnaireCounter = inject(PublicBoxQuestionnaireCounterService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly httpError = inject(HttpErrorService);
  private readonly navigator = inject(NavigatorService);
  private readonly toast = inject(ToastService);
  private readonly pendingQuestionnaireEndRequested = signal(false);
  private readonly pendingSaveRequested = signal(false);
  private latestChangedOptionKey: Nillable<string> = null;
  private questionnaireEndTimeoutId: ReturnType<typeof setTimeout> | null = null;

  protected readonly Icon = Icon;
  protected readonly BoxQuestionnaireStatus = BoxQuestionnaireStatus;
  protected readonly isEnding = signal(false);
  protected readonly isLoading = signal(true);
  protected readonly isSaving = signal(false);
  protected readonly savingOptionKey = signal<Nillable<string>>(null);
  // prettier-ignore
  protected readonly questionnaire = signal<Nillable<BoxQuestionnaireDetails>>(null);
  protected readonly remainingCount = signal(0);
  protected readonly selections = signal<QuestionnaireSelections>({});
  protected readonly savedSelectionsSignature = signal('');

  public constructor() {
    this.destroyRef.onDestroy(() => this.clearQuestionnaireEndTimeout());
    this.initializeRemainingCountSubscription();
    this.initializeQuestionnaireDetailsSubscription();
  }

  @HostListener('window:beforeunload', ['$event'])
  public handleBeforeUnload(event: BeforeUnloadEvent): void {
    if (!this.isDirty()) {
      return;
    }

    event.preventDefault();
    event.returnValue = '';
  }

  public canDeactivate(): boolean {
    return !this.isDirty() || this.confirmDiscardChanges();
  }

  protected get isEditable(): boolean {
    return (
      !this.previewMode() &&
      !!this.questionnaire()?.contextOperations.canBeFilled &&
      !this.isEnding()
    );
  }

  protected handleQuestionnaireNavigation(
    questionnaireId: Guid,
    previewMode = false,
  ): void {
    if (!this.canDeactivate()) {
      return;
    }

    this.navigator.navigateByRouterLink(
      (provider) =>
        provider.routerLink(Route.PL_AccountQuestionnaireDetails, {
          [RouteParam.Guid]: questionnaireId,
        }),
      {
        queryParams: previewMode ? { mode: 'preview' } : undefined,
      },
    );
  }

  protected isQuestionSelected(question: BoxQuestionnaireQuestion): boolean {
    return !isNil(this.selectedOptionId(question.id));
  }

  protected isOptionSelected(questionId: Guid, optionId: Guid): boolean {
    return this.selectedOptionId(questionId) === optionId;
  }

  protected optionActionLabel(
    questionId: Guid,
    option: BoxQuestionnaireQuestionOption,
  ): string {
    if (this.isOptionSelected(questionId, option.id)) {
      return 'Twój wybór!';
    }

    if (!option.isAvailable) {
      return 'Wyprzedany';
    }

    return 'Dodaj do boxa';
  }

  protected optionButtonTheme(
    questionId: Guid,
    option: BoxQuestionnaireQuestionOption,
  ): SlimButtonTheme {
    if (this.isOptionSelected(questionId, option.id)) {
      return 'primary';
    }

    if (!option.isAvailable) {
      return 'primary-light';
    }

    return 'neutral';
  }

  protected optionIsSaving(questionId: Guid, optionId: Guid): boolean {
    return this.savingOptionKey() === this.optionKey(questionId, optionId);
  }

  protected optionIsDisabled(option: BoxQuestionnaireQuestionOption): boolean {
    return !this.isEditable || !option.isAvailable || this.isEnding();
  }

  protected questionLabel(
    question: BoxQuestionnaireQuestion,
    index: number,
  ): string {
    return question.description?.trim() || `Wybór: Kosmetyk ${index + 1}`;
  }

  protected handleOptionClick(
    questionId: Guid,
    option: BoxQuestionnaireQuestionOption,
  ): void {
    if (!this.isEditable || !option.isAvailable) {
      return;
    }

    this.selections.update((current) => {
      const selectedOptionId = current[questionId];

      return {
        ...current,
        [questionId]: selectedOptionId === option.id ? null : option.id,
      };
    });
    this.latestChangedOptionKey = this.optionKey(questionId, option.id);

    if (this.isSaving()) {
      this.pendingSaveRequested.set(true);
      return;
    }

    this.saveQuestionnaire();
  }

  private initializeRemainingCountSubscription(): void {
    this.questionnaireCounter.count$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((count) => this.handleRemainingCountChange(count));
  }

  private initializeQuestionnaireDetailsSubscription(): void {
    toObservable(this.questionnaireId)
      .pipe(
        filter(Boolean),
        switchMap((questionnaireId) =>
          this.fetchQuestionnaireDetails(questionnaireId),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((questionnaire) =>
        this.handleQuestionnaireLoaded(questionnaire),
      );
  }

  private fetchQuestionnaireDetails(questionnaireId: Guid) {
    this.isLoading.set(true);

    return this.boxQuestionnaireDataSource.fetchDetails(questionnaireId);
  }

  private handleRemainingCountChange(count: number): void {
    this.remainingCount.set(count);
  }

  private handleQuestionnaireLoaded(
    questionnaire: BoxQuestionnaireDetails,
  ): void {
    this.applyQuestionnaire(questionnaire);
    this.refreshRemainingCount();
    this.scheduleQuestionnaireEnd(questionnaire);
  }

  private applyQuestionnaire(questionnaire: BoxQuestionnaireDetails): void {
    const selections = this.extractSelections(questionnaire);

    this.questionnaire.set(questionnaire);
    this.selections.set(selections);
    this.savedSelectionsSignature.set(this.selectionSignature(selections));
    this.isLoading.set(false);
  }

  private saveQuestionnaire(): void {
    const questionnaire = this.questionnaire();

    if (isNil(questionnaire) || !this.isEditable || this.isSaving()) {
      return;
    }

    const submittedSelections = this.currentSelectedQuestions();

    this.isSaving.set(true);
    this.savingOptionKey.set(this.latestChangedOptionKey);

    this.boxQuestionnaireDataSource
      .fill({
        id: questionnaire.id,
        questions: submittedSelections,
      })
      .subscribe({
        next: (response) => this.handleSaveQuestionnaireSuccess(response),
        error: (error) => this.handleSaveQuestionnaireFailure(error),
      });
  }

  private handleSaveQuestionnaireSuccess(
    response: BoxQuestionnaireDetails,
  ): void {
    this.applyQuestionnaire(response);
    this.resetSavingState();
    this.showSaveQuestionnaireSuccessToast();
    this.refreshRemainingCount();
    this.scheduleQuestionnaireEnd(response);

    if (this.pendingSaveRequested() || this.isDirty()) {
      this.pendingSaveRequested.set(false);
      this.saveQuestionnaire();
      return;
    }

    if (this.shouldEndQuestionnaireAfterSave(response)) {
      this.pendingQuestionnaireEndRequested.set(false);
      this.endQuestionnaire();
      return;
    }

    this.pendingSaveRequested.set(false);
  }

  private shouldEndQuestionnaireAfterSave(
    questionnaire: BoxQuestionnaireDetails,
  ): boolean {
    return (
      questionnaire.status !== BoxQuestionnaireStatus.Ended &&
      (this.pendingQuestionnaireEndRequested() ||
        this.hasQuestionnaireExpired(questionnaire))
    );
  }

  private handleSaveQuestionnaireFailure(error: HttpErrorResponse): void {
    this.showFailureAlertByResponse(error);
    this.resetSavingState();
    this.pendingSaveRequested.set(false);
  }

  private resetSavingState(): void {
    this.isSaving.set(false);
    this.savingOptionKey.set(null);
  }

  private showSaveQuestionnaireSuccessToast(): void {
    this.toast.show((builder) =>
      builder.initSuccess('Zmiany w ankiecie zostały zapisane.').build(),
    );
  }

  private scheduleQuestionnaireEnd(questionnaire: BoxQuestionnaireDetails): void {
    this.clearQuestionnaireEndTimeout();

    if (questionnaire.status === BoxQuestionnaireStatus.Ended) {
      return;
    }

    const delay = questionnaire.endDate.getTime() - Date.now();

    if (delay <= 0) {
      this.handleQuestionnaireEndReached();
      return;
    }

    this.questionnaireEndTimeoutId = setTimeout(() => {
      this.handleQuestionnaireEndReached();
    }, delay);
  }

  private clearQuestionnaireEndTimeout(): void {
    if (this.questionnaireEndTimeoutId) {
      clearTimeout(this.questionnaireEndTimeoutId);
      this.questionnaireEndTimeoutId = null;
    }
  }

  private handleQuestionnaireEndReached(): void {
    if (this.isSaving()) {
      this.pendingQuestionnaireEndRequested.set(true);
      return;
    }

    this.endQuestionnaire();
  }

  private endQuestionnaire(): void {
    const questionnaire = this.questionnaire();

    if (
      isNil(questionnaire) ||
      this.isEnding() ||
      questionnaire.status === BoxQuestionnaireStatus.Ended
    ) {
      return;
    }

    this.isEnding.set(true);
    this.boxQuestionnaireDataSource.endById(questionnaire.id).subscribe({
      next: () => this.handleQuestionnaireEndSuccess(questionnaire.id),
      error: (error) => this.handleQuestionnaireEndFailure(error),
    });
  }

  private handleQuestionnaireEndSuccess(questionnaireId: Guid): void {
    this.pendingQuestionnaireEndRequested.set(false);

    this.boxQuestionnaireDataSource.fetchDetails(questionnaireId).subscribe({
      next: (questionnaire) => {
        this.isEnding.set(false);
        this.handleQuestionnaireLoaded(questionnaire);

        if (!this.previewMode()) {
          this.handleQuestionnaireNavigation(questionnaireId, true);
        }
      },
      error: () => {
        this.isEnding.set(false);

        if (!this.previewMode()) {
          this.handleQuestionnaireNavigation(questionnaireId, true);
        }
      },
    });
  }

  private handleQuestionnaireEndFailure(error: HttpErrorResponse): void {
    this.pendingQuestionnaireEndRequested.set(false);
    this.isEnding.set(false);
    this.showFailureAlertByResponse(error);
  }

  private hasQuestionnaireExpired(questionnaire: BoxQuestionnaireDetails): boolean {
    return questionnaire.endDate.getTime() <= Date.now();
  }

  private confirmDiscardChanges(): boolean {
    return window.confirm(
      'Masz niezapisane zmiany w ankiecie. Czy na pewno chcesz opuścić ten widok?',
    );
  }

  private currentSelectedQuestions(): {
    id: Guid;
    selectedOptionId: Guid;
  }[] {
    const questionnaire = this.questionnaire();

    if (isNil(questionnaire)) {
      return [];
    }

    return questionnaire.questions
      .map((question) => {
        const selectedOptionId = this.selectedOptionId(question.id);

        return isNil(selectedOptionId)
          ? null
          : {
              id: question.id,
              selectedOptionId,
            };
      })
      .filter((item): item is { id: Guid; selectedOptionId: Guid } => !!item);
  }

  private extractSelections(
    questionnaire: BoxQuestionnaireDetails,
  ): QuestionnaireSelections {
    return questionnaire.questions.reduce<QuestionnaireSelections>(
      (accumulator, question) => {
        const selectedOption = question.options.find(
          (option) => option.isSelected,
        );

        accumulator[question.id] = selectedOption?.id ?? null;

        return accumulator;
      },
      {},
    );
  }

  private isDirty(): boolean {
    return (
      !this.isEnding() &&
      this.isEditable &&
      this.selectionSignature(this.selections()) !==
        this.savedSelectionsSignature()
    );
  }

  private refreshRemainingCount(): void {
    this.questionnaireCounter.refresh().subscribe();
  }

  private selectedOptionId(questionId: Guid): Nillable<Guid> {
    return this.selections()[questionId] ?? null;
  }

  private optionKey(questionId: Guid, optionId: Guid): string {
    return `${questionId}:${optionId}`;
  }

  private selectionSignature(selections: QuestionnaireSelections): string {
    return Object.entries(selections)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([questionId, optionId]) => `${questionId}:${optionId ?? ''}`)
      .join('|');
  }

  private showFailureAlertByResponse(response: HttpErrorResponse): void {
    const messages = this.httpError.extract(response).messages();

    this.alerts.showMultiple((builder) =>
      messages.map((message) => builder.buildFailure(message)),
    );
  }
}
